import {
  useIsLoggedIn,
  useSessionTyped,
  useUserIdWithLoading,
} from '@dreamstack/auth'
import { useLocale, useTranslation } from '@dreamstack/i18n'
import { useTrackFormSubmissionGA4 } from '@dreamstack/tracking'
import { getOrCreateAnonymousId } from '@dreamstack/tracking/src/getOrCreateAnonymousId'
import { trpc } from '@dreamstack/trpc/client'
import { useRouter } from 'next/router'
import type { FunctionComponent } from 'react'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { AccessFileForm } from '../accessFile/AccessFileForm'
import { getAccessFileUrls } from '../accessFile/getAccessFileUrls'
import type { AccessFileFormData } from '../accessFile/useAccessFileFormSchema'
import { useAccessFileFormSchema } from '../accessFile/useAccessFileFormSchema'
import { useSuccessDialog } from '../confirmation/useSuccessDialog'
import { NewsletterType } from '../newsletter/NewsletterTypes.static'

const baseUrl = process.env.NEXT_PUBLIC_NEXT_BASE_URL

export const HomeOwnershipOrderReportForm: FunctionComponent<
  React.PropsWithChildren<{}>
> = () => {
  const yupSchemaResolver = useAccessFileFormSchema()
  const methods = useForm({ resolver: yupSchemaResolver })
  const t = useTranslation()
  const router = useRouter()
  const locale = useLocale()
  const isLoggedIn = useIsLoggedIn()
  const trackFormSubmissionForGA4 = useTrackFormSubmissionGA4()
  const { userId } = useUserIdWithLoading()
  const session = useSessionTyped()
  const { mutateAsync: sendHomeownerMail, isPending: loadingHomeOwner } =
    trpc.newsletter.sendHomeOwnershipReport.useMutation()

  const {
    mutateAsync: sendNewsletterRegistration,
    isPending: loadingNewsletter,
  } = trpc.newsletter.sendRegisterNewsletter.useMutation()

  const { mutateAsync: trackFormSubmissionServer } =
    trpc.anonymousTracking.trackEvent.useMutation()

  const somethingIsLoading = useMemo(() => {
    return loadingHomeOwner || loadingNewsletter
  }, [loadingHomeOwner, loadingNewsletter])

  const redirectUrl = getAccessFileUrls(locale, 'homeOwnership', 'redirect')

  const returnToLandingpage = () => {
    router.push(redirectUrl)
  }

  const { SuccessDialog, show } = useSuccessDialog({
    title: t('accentro:homeOwnershipReportForm.modalTitle'),
    text: t('accentro:homeOwnershipReportForm.modalBody'),
    ctaLabel: t('accentro:homeOwnershipReportForm.modalCta'),
    onCtaClick: returnToLandingpage,
  })

  // TODO: Refactor to one place. See other comments.
  const { data: jwtHrefData } = trpc.user.getJwtForOptInAccess.useQuery(
    { type: 'HomeOwnershipReport' },
    {
      enabled: isLoggedIn,
      trpc: { ssr: false },
      staleTime: 0,
    }
  )

  const { handleSubmit, control, reset } = methods

  // TODO: Refactor to one place. See other comments.
  const onSubmit = useCallback(
    async (data: AccessFileFormData) => {
      const actions = []
      const anonymousUserId = getOrCreateAnonymousId()
      const trackingData = {
        formId: 'homeOwnershipReport',
        formData: {
          ...data,
          locale,
        },
      }

      if (!isLoggedIn)
        actions.push(
          sendHomeownerMail({
            email: data.clientEmail,
            locale,
          }),
          trackFormSubmissionServer({
            eventName: 'formSubmission',
            userId,
            anonymousUserId,
            data: trackingData,
          })
        )

      if (data.subscribeNewsletter) {
        const email = isLoggedIn ? session?.email : data.clientEmail
        if (email) {
          actions.push(
            sendNewsletterRegistration({
              email,
              type: NewsletterType.Newsletter,
              locale,
              wantsNewsletter: true,
            })
          ),
            trackFormSubmissionServer({
              eventName: 'formSubmission',
              userId,
              anonymousUserId,
              data: {
                formId: 'Registered for Newsletter',
                formData: {
                  email: data.clientEmail,
                },
              },
            })
        }
      }

      await Promise.all(actions)

      trackFormSubmissionForGA4(trackingData)

      !isLoggedIn && show()
      if (isLoggedIn) {
        window.location.href = `${baseUrl}/${jwtHrefData?.href}`
      }
    },
    [
      isLoggedIn,
      jwtHrefData?.href,
      locale,
      sendHomeownerMail,
      sendNewsletterRegistration,
      session?.email,
      show,
      trackFormSubmissionServer,
      trackFormSubmissionForGA4,
      userId,
    ]
  )

  return (
    <>
      {SuccessDialog}
      <AccessFileForm
        methods={methods}
        handleSubmit={handleSubmit}
        control={control}
        onSubmit={onSubmit}
        loading={somethingIsLoading}
      />
    </>
  )
}
